import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase, supabaseAdmin } from '@/lib/supabase';
import type { Organization } from '@/lib/database.types';
import { extractPreset, type ThemePreset } from '@/lib/theme';

export interface OrganizationWithPreset {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  created_at: string | null;
  preset: ThemePreset;
}

export function useOrganizationsWithPreset() {
  return useQuery({
    queryKey: ['themes', 'organizations'],
    queryFn: async (): Promise<OrganizationWithPreset[]> => {
      const { data, error } = await supabaseAdmin
        .from('organizations')
        .select('id, name, slug, logo_url, settings, created_at')
        .order('name', { ascending: true });

      if (error) throw error;

      return ((data || []) as unknown as Organization[]).map((org) => ({
        id: org.id,
        name: org.name,
        slug: org.slug,
        logo_url: org.logo_url,
        created_at: org.created_at,
        preset: extractPreset(org.settings),
      }));
    },
  });
}

export function useUpdateOrgThemePreset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      organizationId,
      preset,
    }: {
      organizationId: string;
      preset: ThemePreset;
    }) => {
      const { error } = await supabase.rpc('update_org_theme_preset' as never, {
        p_org_id: organizationId,
        p_preset: preset,
      } as never);
      if (error) throw error;
      return { organizationId, preset };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['themes', 'organizations'] });
      queryClient.invalidateQueries({ queryKey: ['themes', 'audit'] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

export interface ThemeAuditEntry {
  id: string;
  organization_id: string;
  organization_name: string | null;
  from_preset: ThemePreset | null;
  to_preset: ThemePreset;
  admin_user_id: string | null;
  admin_user_name: string | null;
  created_at: string;
}

export function useThemePresetAudit(limit = 25) {
  return useQuery({
    queryKey: ['themes', 'audit', limit],
    queryFn: async (): Promise<ThemeAuditEntry[]> => {
      const { data, error } = await supabaseAdmin
        .from('admin_audit_log')
        .select(
          'id, admin_user_id, target_id, details, created_at, admin_users(name)'
        )
        .eq('action', 'theme_preset_changed' as never)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      const rows = (data || []) as unknown as Array<{
        id: string;
        admin_user_id: string | null;
        target_id: string | null;
        details: { from?: string | null; to?: string | null } | null;
        created_at: string;
        admin_users: { name: string | null } | null;
      }>;

      const orgIds = Array.from(
        new Set(rows.map((r) => r.target_id).filter((id): id is string => !!id))
      );

      let orgNames: Record<string, string> = {};
      if (orgIds.length > 0) {
        const { data: orgs, error: orgErr } = await supabaseAdmin
          .from('organizations')
          .select('id, name')
          .in('id', orgIds as never);
        if (orgErr) throw orgErr;
        orgNames = Object.fromEntries(
          ((orgs || []) as unknown as Array<{ id: string; name: string }>).map(
            (o) => [o.id, o.name]
          )
        );
      }

      return rows
        .filter((r) => r.target_id)
        .map((row) => ({
          id: row.id,
          organization_id: row.target_id as string,
          organization_name: orgNames[row.target_id as string] ?? null,
          from_preset: (row.details?.from as ThemePreset | null) ?? null,
          to_preset: ((row.details?.to as ThemePreset | null) ?? 'blaze'),
          admin_user_id: row.admin_user_id,
          admin_user_name: row.admin_users?.name ?? null,
          created_at: row.created_at,
        }));
    },
  });
}

export function useThemeStats(orgs: OrganizationWithPreset[] | undefined) {
  return useMemo(() => {
    if (!orgs) return { blaze: 0, lightspeed: 0, total: 0 };
    return orgs.reduce(
      (acc, org) => {
        acc[org.preset] += 1;
        acc.total += 1;
        return acc;
      },
      { blaze: 0, lightspeed: 0, total: 0 }
    );
  }, [orgs]);
}
